import { PersonType } from '@/types/api';
import {
  FxCategory,
  FxVendor,
  FxSubscription,
  FxTransaction,
  FxTransactionImport,
  FxCategorizationRule,
  FxCategorizationEvent,
  FxBankConnection,
  FxBankAccount,
  FxInvoice,
} from '@/types/api/finance';

/**
 * Map a snake_case `profiles` DB row into the camelCase PersonType shape.
 * This is the single canonical mapping — all services should use this
 * instead of defining their own local version.
 *
 * Handles partial rows gracefully (e.g. when only a few columns are selected).
 */
export function mapProfileToPerson(row: any): PersonType {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email ?? null,
    pictureUrl: row.picture_url ?? null,
    linkedinUrl: row.linkedin_url ?? '',
    githubUrl: row.github_url ?? null,
    developerType: row.developer_type ?? null,
    experience: row.experience ?? '',
    englishLevel: row.english_level ?? null,
    availability: row.availability ?? null,
    salaryExpectation: row.salary_expectation ?? '',
    source: row.source ?? '',
    referredBy: row.referred_by ?? '',
    status: row.status ?? null,
    rating: row.overall_rating ?? 0,
    communicationSkillsRating: row.communication_rating ?? 0,
    softSkillsRating: row.soft_skills_rating ?? 0,
    technicalSkillsRating: row.technical_rating ?? 0,
    summary: row.summary ?? null,
    country: row.country ?? null,
    state: row.state ?? null,
    city: row.city ?? '',
    addressLine1: row.address_line1 ?? null,
    addressLine2: row.address_line2 ?? null,
    zipCode: row.zip_code ?? null,
    mobilePhone: row.phone ?? '',
    birthdate: row.birthdate ?? null,
    gender: row.gender ?? null,
    ethnicity: row.ethnicity ?? null,
    emergencyContactName: row.emergency_contact_name ?? null,
    emergencyContactPhone: row.emergency_contact_phone ?? null,
    emergencyContactEmail: row.emergency_contact_email ?? null,
    emergencyContactRelationship: row.emergency_contact_relationship ?? null,
    employeeWorkStatus: row.employee_work_status ?? null,
    workEmail: row.work_email ?? null,
    candidateOwner: null,
    owner: null,
    ownerId: row.owner_id ?? '',
    isTalentPool: row.is_talent_pool ?? false,
    trioProfileUrl: row.trio_profile_url ?? null,
    hasFintechExperience: row.has_fintech_experience ?? false,
    skills: row.skills ?? [],
    mainTechStack: row.mainTechStack ?? [],
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
    deletedAt: row.deleted_at ?? null,
  };
}

/**
 * Map a partial profile row (e.g. from a join that only selected a few columns).
 * Returns null if the row is null/undefined.
 * Useful for joined profile data in interview_reviewers, team_members, etc.
 */
// --- FinanceX mappers ---

export function mapFxCategory(row: any): FxCategory {
  return {
    id: row.id,
    name: row.name,
    slug: row.slug,
    type: row.type ?? 'expense',
    parentId: row.parent_id ?? null,
    color: row.color ?? null,
    icon: row.icon ?? null,
    sortOrder: row.sort_order ?? 0,
    isActive: row.is_active ?? true,
  };
}

export function mapFxVendor(row: any): FxVendor {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    name: row.name,
    displayName: row.display_name ?? null,
    website: row.website ?? null,
    logoUrl: row.logo_url ?? null,
    categoryId: row.category_id ?? null,
    category: row.fx_categories ? mapFxCategory(row.fx_categories) : null,
    profileId: row.profile_id ?? null,
    teamId: row.team_id ?? null,
    notes: row.notes ?? null,
    isActive: row.is_active ?? true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxSubscription(row: any): FxSubscription {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    vendorId: row.vendor_id,
    vendor: row.fx_vendors ? mapFxVendor(row.fx_vendors) : null,
    name: row.name,
    amount: parseFloat(row.amount),
    currency: row.currency ?? 'USD',
    billingCycle: row.billing_cycle,
    startDate: row.start_date,
    endDate: row.end_date ?? null,
    nextBilling: row.next_billing ?? null,
    autoRenew: row.auto_renew ?? true,
    seats: row.seats ?? null,
    status: row.status ?? 'active',
    owner: row.owner ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxTransaction(row: any): FxTransaction {
  return {
    id: row.id,
    date: row.date,
    description: row.description,
    amount: parseFloat(row.amount),
    type: row.type ?? 'expense',
    currency: row.currency ?? 'USD',
    vendorId: row.vendor_id ?? null,
    vendor: row.fx_vendors ? mapFxVendor(row.fx_vendors) : null,
    categoryId: row.category_id ?? null,
    category: row.fx_categories ? mapFxCategory(row.fx_categories) : null,
    subscriptionId: row.subscription_id ?? null,
    importId: row.import_id ?? null,
    bankAccountId: row.bank_account_id ?? null,
    externalId: row.external_id ?? null,
    source: row.source ?? 'manual',
    aiConfidence: row.ai_confidence != null ? parseFloat(row.ai_confidence) : null,
    aiSuggested: row.ai_suggested ?? false,
    userVerified: row.user_verified ?? false,
    reviewStatus: row.review_status ?? 'pending',
    matchSource: row.match_source ?? null,
    matchedRuleId: row.matched_rule_id ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxTransactionImport(row: any): FxTransactionImport {
  return {
    id: row.id,
    fileName: row.file_name,
    sourceAccount: row.source_account ?? null,
    rowCount: row.row_count ?? 0,
    matchedCount: row.matched_count ?? 0,
    aiMatchedCount: row.ai_matched_count ?? 0,
    unmatchedCount: row.unmatched_count ?? 0,
    status: row.status ?? 'pending',
    importedBy: row.imported_by ?? null,
    errorMessage: row.error_message ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxCategorizationRule(row: any): FxCategorizationRule {
  return {
    id: row.id,
    name: row.name ?? null,
    matchType: row.match_type,
    matchPattern: row.match_pattern,
    vendorId: row.vendor_id ?? null,
    vendor: row.fx_vendors ? mapFxVendor(row.fx_vendors) : null,
    categoryId: row.category_id ?? null,
    category: row.fx_categories ? mapFxCategory(row.fx_categories) : null,
    source: row.source ?? 'user',
    priority: row.priority ?? 0,
    hitCount: row.hit_count ?? 0,
    overrideCount: row.override_count ?? 0,
    lastHitAt: row.last_hit_at ?? null,
    lastOverrideAt: row.last_override_at ?? null,
    isActive: row.is_active ?? true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxCategorizationEvent(row: any): FxCategorizationEvent {
  return {
    id: row.id,
    transactionId: row.transaction_id ?? null,
    eventType: row.event_type,
    prevVendorId: row.prev_vendor_id ?? null,
    prevCategoryId: row.prev_category_id ?? null,
    prevMatchSource: row.prev_match_source ?? null,
    newVendorId: row.new_vendor_id ?? null,
    newCategoryId: row.new_category_id ?? null,
    ruleId: row.rule_id ?? null,
    aiConfidence: row.ai_confidence != null ? parseFloat(row.ai_confidence) : null,
    description: row.description ?? null,
    amount: row.amount != null ? parseFloat(row.amount) : null,
    createdAt: row.created_at,
  };
}

export function mapFxBankConnection(row: any): FxBankConnection {
  return {
    id: row.id,
    institutionName: row.institution_name,
    plaidItemId: row.plaid_item_id,
    plaidAccessToken: row.plaid_access_token,
    status: row.status ?? 'active',
    lastSyncedAt: row.last_synced_at ?? null,
    settings: row.settings ?? {},
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxBankAccount(row: any): FxBankAccount {
  return {
    id: row.id,
    connectionId: row.connection_id,
    plaidAccountId: row.plaid_account_id,
    name: row.name,
    accountType: row.account_type,
    institutionName: row.institution_name,
    accountNumberLast4: row.account_number_last4 ?? null,
    currentBalance: row.current_balance != null ? parseFloat(row.current_balance) : null,
    currency: row.currency ?? 'USD',
    isActive: row.is_active ?? true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapFxInvoice(row: any): FxInvoice {
  return {
    id: row.id,
    shortId: row.short_id ?? row.id,
    invoiceNumber: row.invoice_number,
    clientName: row.client_name,
    vendorId: row.vendor_id ?? null,
    vendor: row.fx_vendors ? mapFxVendor(row.fx_vendors) : null,
    teamId: row.team_id ?? null,
    amount: parseFloat(row.amount),
    openBalance: parseFloat(row.open_balance ?? '0'),
    date: row.date,
    dueDate: row.due_date ?? null,
    billingPeriod: row.billing_period ?? null,
    status: row.status ?? 'open',
    matchedTransactionId: row.matched_transaction_id ?? null,
    memo: row.memo ?? null,
    source: row.source ?? 'manual',
    externalId: row.external_id ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapPartialProfile(row: any): { id: string; firstName: string; lastName: string; email: string | null; pictureUrl: string | null; developerType: string | null; skills: string[] } | null {
  if (!row) return null;
  return {
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email ?? null,
    pictureUrl: row.picture_url ?? null,
    developerType: row.developer_type ?? null,
    skills: row.skills ?? [],
  };
}
